import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { FormCheckbox, FormAjaxSelect } from '@core/components/form';
import { useObservable, useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useBloc } from '@core/utils/bloc';
import { GlossaryBlockWidgetForm, GlossaryBlockVariation } from './GlossaryBlock';
import { GlossaryBlockBloc } from './GlossaryBlockBloc';
import { GlossaryDefinitionResponse, GlossaryResponse } from '@business/entities/glossary';
import { R } from '@core/utils/r';
import { Card, CardBody } from 'reactstrap';
import { DefinitionForm } from '../LibraryBlock';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { GlossaryAPI } from '@business/api/glossary';
import { map, switchMap } from 'rxjs/operators';
import { CommonService } from '@business/services';
import { of } from 'rxjs';
import { useInterval } from '@core/utils/react';

export interface GlossaryBlockSettingsProps {
  form: GlossaryBlockWidgetForm;
  variation: GlossaryBlockVariation;
  id: string;
  lessonId?: string;
}

export const GlossaryBlockSettings = memo((props: GlossaryBlockSettingsProps) => {
  const { form, lessonId } = props;
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(GlossaryBlockBloc, formValue.search);
  const glossary = useObservable(bloc.item$);
  const [definitions, setDefinitions] = useState<GlossaryDefinitionResponse[]>([]);
  const [glossaries, setGlossaries] = useState<GlossaryResponse[]>([]);
  const definitionItems = useObservable(form.controls.definitions.items$, []);
  const loading = useLoadingState(bloc.loading);

  useEffect(() => {
    if (glossary) {
      setDefinitions(glossary.definitions);
    }
  }, [glossary]);

  const initialValue = useMemo(() => {
    if (formValue.search && formValue.searchValue) {
      return { label: formValue.searchValue, value: formValue.search };
    }
    return undefined;
  }, [formValue.search, formValue.searchValue]);

  useEffect(() => {
    form.patchValue({
      definitions: R.map(definitions || [], d => ({
        definitionId: d.id,
        option: R.find(formValue?.definitions || [], f => f.definitionId === d.id)?.option || false,
      })),
    });
  }, [definitions, form, formValue, formValue.definitions, props]);

  const onFormChange = useCallback(() => {
    setTimeout(() => {
      if (lessonId) {
        return bloc
          .onReload(lessonId)
          .pipe(
            switchMap(res => {
              const blocks = R.get(res, 'data.blocks');
              if (blocks && !!blocks.length) {
                const block = R.first(R.filter(blocks, b => b.id == props.id));
                if (block) {
                  const data = block.data;
                  form.patchValue({ text: data.text });
                }
              }
              return of(null);
            }),
          )
          .subscribe();
      }
    }, 6000);
  }, [bloc, form, lessonId, props.id]);

  const onChange = useCallback(
    (value: string) => {
      const glossary = R.first(R.filter(glossaries, (g: GlossaryResponse) => g.id == value)) as GlossaryResponse;
      setDefinitions(glossary?.definitions || []);
      form.patchValue({ searchValue: glossary?.title, heading: glossary?.title });
      onFormChange();
    },
    [form, glossaries, onFormChange],
  );

  const onSearch = useCallback((search: string) => {
    return GlossaryAPI.searchGlossary(search).pipe(
      map(items => {
        setGlossaries(items);
        return CommonService.selectItems(items, 'title', 'id');
      }),
    );
  }, []);

  return (
    <div>
      <FormAjaxSelect initalValue={initialValue} control={form.controls.search} callback={search => onSearch(search)} onChange={onChange} />
      {!!definitions.length &&
        definitionItems.map((ctrl, i) => {
          return <GlossaryDefinitionItem ctrl={ctrl} key={i} definition={definitions[i] && definitions[i]} onDefinitionClick={onFormChange} />;
        })}
    </div>
  );
});

export interface GlossaryDefinitionItemProps {
  ctrl: DefinitionForm;
  definition?: GlossaryDefinitionResponse;
  onDefinitionClick(): void;
}

export const GlossaryDefinitionItem = memo((props: GlossaryDefinitionItemProps) => {
  const { definition, ctrl, onDefinitionClick } = props;
  return definition ? (
    <Card className="mb-2 ha-item" style={{ cursor: 'pointer' }}>
      <CardBody>
        <FormCheckbox control={ctrl.controls.option} noPadding onChange={onDefinitionClick}>
          <div>{definition?.body || ''}</div>
        </FormCheckbox>
      </CardBody>
    </Card>
  ) : null;
});
